import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Chat,
  ChatIcon,
  ChatToggle,
  LayoutContextProvider,
  LiveKitRoom,
  RoomAudioRenderer,
  useIsRecording,
  useRoomContext,
} from '@livekit/components-react';
import { ConnectionState, RoomEvent, RpcError, Track } from 'livekit-client';
import type { Participant } from 'livekit-client';
import { fetchRoomRecording } from './api';
import type { JoinGrant, LiveTrack } from './api';
import { InviteButton } from './InviteButton';
import { Mark } from './Mark';
import { CallStage } from './Stage';
import { QualityMenu } from './QualityMenu';
import { statsOverlay } from './StatsReadout';
import { brand } from './brand';
import { forgetGrants, granted } from './grants';
import { verifiedSigned } from './signed';
import type { Route } from './routes';

/**
 * The banner that says a meeting is being taped.
 *
 * Driven by LiveKit's own room-level recording flag, which is set because the
 * recorder's token carries `Recorder: true` (see internal/token). So this is not
 * a hopeful piece of UI we remember to render — it tracks the server's actual
 * state, and it lights up for exactly as long as something is genuinely
 * subscribed and writing.
 *
 * Must be rendered INSIDE LiveKitRoom: useIsRecording reads the room context.
 */
/**
 * The header's own line: how many people are here, how long it has been going,
 * and whether it is being taped.
 *
 * Must be rendered INSIDE LiveKitRoom — everything it reports comes from the
 * room context.
 */
function CallMeta() {
  const isRecording = useIsRecording();
  const { people, elapsed, comrades } = useCallMeta();
  return (
    <span className="mx-callmeta">
      <span className="mx-callmeta-count">
        {people} joined
      </span>
      <span className="mx-dotsep">·</span>
      <span className="mx-callmeta-clock">{elapsed}</span>
      {/* Comrades held in the call from the host (MWE-13): seen, never on
          the stage and never counted, and marked for what they are. */}
      {comrades.map((name) => (
        <span key={name} className="mx-comrade" title={`${name} is an AI, in the call from the host`}>
          <b>AI</b>
          {name}
        </span>
      ))}
      {isRecording && (
        <span className="mx-rec" title="Recording — one track per person">
          <i aria-hidden="true" />
          REC
        </span>
      )}
    </span>
  );
}

function RecordingBanner() {
  const isRecording = useIsRecording();
  const notice = brand().recordingNotice;
  if (!isRecording) return null;
  return (
    <div className="mx-recording" role="status">
      <span className="mx-recording-dot" aria-hidden="true" />
      <span>
        {/* The storage sentence belongs to the operator: "our own storage" is the
            reason to self-host, but only they can say whose. Unset, we state the
            fact and make no claim about where it goes. */}
        <strong>Recording.</strong> This meeting is being recorded — one audio and video track
        per person.{notice ? ` ${notice}` : ''}
      </span>
    </div>
  );
}

/**
 * How many people are here, and how long it has been going.
 *
 * Counted from the room rather than from anything we remember: the SFU is the
 * only thing that knows, and a number the client maintained would be wrong in
 * exactly the moments somebody looks at it. The recorder does not appear here —
 * it joins Hidden, so LiveKit never delivers it to another participant, and the
 * count is humans by construction rather than by filtering.
 */
function useCallMeta(startedAt?: string): { people: number; elapsed: string; comrades: string[] } {
  const room = useRoomContext();
  const [people, setPeople] = useState(1);
  const [comrades, setComrades] = useState<string[]>([]);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    // Agents are not people: see Stage (MWE-1).
    const update = () => {
      const remote = [...room.remoteParticipants.values()];
      setPeople(remote.filter((p) => !p.isAgent).length + 1);
      // A comrade's presence is an agent under agent:<user>:<name> (MWE-13);
      // the momentary say participant (ctl:…) is an agent too, and not shown.
      setComrades(
        remote
          .filter((p) => p.isAgent && p.identity.startsWith('agent:'))
          .map((p) => p.name || p.identity.slice(p.identity.lastIndexOf(':') + 1))
          .sort(),
      );
    };
    update();
    room.on(RoomEvent.ParticipantConnected, update);
    room.on(RoomEvent.ParticipantDisconnected, update);
    room.on(RoomEvent.Connected, update);
    return () => {
      room.off(RoomEvent.ParticipantConnected, update);
      room.off(RoomEvent.ParticipantDisconnected, update);
      room.off(RoomEvent.Connected, update);
    };
  }, [room]);

  // Our own arrival, as the fallback clock. Held in a ref so a re-render does
  // not restart the timer at zero.
  const joinedAt = useRef(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const from = startedAt ? Date.parse(startedAt) : joinedAt.current;
  const secs = Math.max(0, Math.floor((now - from) / 1000));
  const elapsed = [Math.floor(secs / 3600), Math.floor((secs % 3600) / 60), secs % 60]
    .map((n) => String(n).padStart(2, '0'))
    .join(':');

  return { people, elapsed, comrades };
}

/**
 * Who is speaking, by participant identity.
 *
 * Driven by the SFU's own active-speaker events rather than by sampling audio
 * levels in the browser: it is the same signal the tiles use, it costs nothing,
 * and it is right about a muted microphone.
 */
function useSpeaking(): Set<string> {
  const room = useRoomContext();
  const [speaking, setSpeaking] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const onChange = (speakers: Participant[]) =>
      setSpeaking(new Set(speakers.map((p) => p.identity)));
    room.on(RoomEvent.ActiveSpeakersChanged, onChange);
    return () => {
      room.off(RoomEvent.ActiveSpeakersChanged, onChange);
    };
  }, [room]);

  return speaking;
}

function bytes(n: number): string {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v < 10 && i > 0 ? v.toFixed(1) : Math.round(v)} ${u[i]}`;
}

/** How often to ask the portal what the recorder has written. */
const RAIL_REFRESH_MS = 4000;

/**
 * What the server is writing, beside the call that is producing it.
 *
 * THIS IS THE DISCLOSURE, and it is a stronger one than any banner. "This
 * meeting is being recorded" is a sentence you can put on a product that
 * composites everyone into one file and runs a model over it afterwards. A list
 * of four files with four people's names on them, growing, is a claim only this
 * architecture can make — and it is checkable by the person it is about.
 *
 * The numbers come from the portal, which reads the manifest the recorder is
 * already keeping and stats the files. The animation comes from the SFU's
 * active-speaker events. Neither needed a new connection between the recorder
 * and anything else.
 */
function RecordingRail({ room }: { room: string }) {
  const [tracks, setTracks] = useState<LiveTrack[] | null>(null);
  const [startedAt, setStartedAt] = useState<string | undefined>();
  const [recording, setRecording] = useState(false);
  const speaking = useSpeaking();

  useEffect(() => {
    let cancelled = false;
    const poll = () => {
      if (document.visibilityState !== 'visible') return;
      fetchRoomRecording(room)
        .then((r) => {
          if (cancelled) return;
          setRecording(r.recording);
          setTracks(r.tracks ?? []);
          setStartedAt(r.startedAt);
        })
        // A failure here must not replace the call with an error. The rail
        // simply stops updating, which is the correct amount of drama for a
        // panel that is telling you about files.
        .catch(() => {});
    };
    poll();
    const t = setInterval(poll, RAIL_REFRESH_MS);
    const onVisible = () => document.visibilityState === 'visible' && poll();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [room]);

  if (!recording || !tracks) return null;
  const mics = tracks.filter((t) => t.kind === 'audio').length;

  return (
    <aside className="mx-rail" aria-label="What is being recorded">
      <div className="mx-rail-head">
        <span>Writing</span>
        <span>
          {tracks.length} track{tracks.length === 1 ? '' : 's'}
        </span>
      </div>

      {tracks.length === 0 ? (
        <p className="mx-rail-empty">Waiting for the first track.</p>
      ) : (
        tracks.map((t) => (
          <div className="mx-trk-live" key={t.file}>
            <span className="mx-trk-who">{t.name}</span>
            <span className="mx-trk-size">{bytes(t.bytes)}</span>
            <span className="mx-trk-file">{t.file}</span>
            <span
              className={`mx-wave${speaking.has(t.identity) ? ' live' : ' quiet'}`}
              aria-hidden="true"
            >
              {Array.from({ length: 14 }, (_, i) => (
                <i key={i} />
              ))}
            </span>
          </div>
        ))
      )}

      <p className="mx-rail-foot">
        {/* Microphones are the AUDIO files only: the list also carries each
            camera's video file, and counting those as microphones overstated
            the room by however many cameras were on. */}
        {mics} microphone{mics === 1 ? '' : 's'}, {tracks.length} file
        {tracks.length === 1 ? '' : 's'}. No compositing, no re-encode, and nothing to un-mix
        later.
        {startedAt && ` Since ${new Date(startedAt).toLocaleTimeString()}.`}
      </p>
    </aside>
  );
}

/** The LiveKit data topic the recorder publishes transcript lines on. Must match
 * TranscriptTopic in cmd/recorder. Subscribing by topic means an unrelated
 * future use of the data channel does not arrive looking like a transcript. */
const TRANSCRIPT_TOPIC = 'meetnix.transcript';

/** How many lines the panel keeps. A long meeting is thousands, and the browser
 * is not the archive — the transcript on the server is. This is the window a
 * person can actually read back during a call. */
const TRANSCRIPT_WINDOW = 300;

type TranscriptLine = {
  /** 'transcript' for text; 'gap' for a stretch that was said but is not on
   * screen (see publishGap in cmd/recorder — a gap carries no text on purpose);
   * 'note' for a running note written by the facilitator. */
  type?: string;
  principal?: string;
  speaker: string;
  at: number;
  text?: string;
  /** The utterance a line belongs to: its track and segment. A 'partial' is
   * replaced by the next partial with the same pair, and retired by the final
   * that carries it (MN-47). */
  track?: string;
  utt?: number;
  /** On a partial: how much of text the transcriber has committed to. */
  stable_upto?: number;
  /** On a partial: how much of the utterance's audio it was decoded from, in
   * ns. Grows within an utterance, so it says which of two partials is newer
   * (MWE-7). */
  audio_ns?: number;
};

/** The key a partial is replaced under. */
const uttKey = (l: TranscriptLine) => `${l.track}\u0000${l.utt}`;

/** Whether partial a is newer than b, of the same utterance. By audio_ns,
 * which only grows, when both carry it; otherwise, from a recorder before it,
 * by the committed prefix, which a later partial never shortens. */
const newerPartial = (a: TranscriptLine, b: TranscriptLine): boolean =>
  a.audio_ns !== undefined && b.audio_ns !== undefined
    ? a.audio_ns >= b.audio_ns
    : (a.stable_upto ?? 0) >= (b.stable_upto ?? 0);

/** A partial split at its committed prefix. stable_upto counts UTF-8 BYTES
 * (it comes from Go), and a JS string is UTF-16, so slicing the string by it
 * would cut "Größe" in the wrong place; it is applied to the bytes instead. A
 * cut inside a character leaves that character to the tail. */
function splitStable(text: string, upto: number): [string, string] {
  const bytes = new TextEncoder().encode(text);
  const head = new TextDecoder().decode(bytes.slice(0, Math.max(0, upto))).replace(/\uFFFD+$/, '');
  return [head, text.slice(head.length)];
}

function PartialText({ text, upto }: { text: string; upto: number }) {
  const [firm, tail] = splitStable(text, upto);
  return (
    <span className="mx-tx-text">
      {firm}
      <span className="mx-tx-pending">{tail}</span>
    </span>
  );
}

/** How close a speaker's next fragment must start to join the row before it. */
const MERGE_GAP_S = 8;
/** A row stops growing here, so a monologue still reads as paragraphs. */
const MERGE_MAX_CHARS = 320;

/**
 * One row per stretch of one person speaking, not one per transcriber segment.
 *
 * The recorder cuts audio into fixed-length segments and each comes back as
 * its own line, so a sentence that straddles a cut arrived as two rows — "The"
 * and then "weather today is…" — which reads as a stutter. Display only: the
 * lines themselves, and the transcript on the server, are untouched.
 */
function mergeFragments(lines: TranscriptLine[]): TranscriptLine[] {
  const out: TranscriptLine[] = [];
  let lastAt = 0;
  for (const l of lines) {
    const prev = out[out.length - 1];
    if (
      prev &&
      l.type === 'transcript' &&
      prev.type === 'transcript' &&
      prev.speaker === l.speaker &&
      prev.principal === l.principal &&
      l.at - lastAt <= MERGE_GAP_S &&
      (prev.text?.length ?? 0) < MERGE_MAX_CHARS
    ) {
      out[out.length - 1] = { ...prev, text: `${prev.text} ${l.text}` };
    } else {
      out.push(l);
    }
    lastAt = l.at;
  }
  return out;
}

/**
 * The live transcript, as it is being said.
 *
 * NO POLLING AND NO NEW CONNECTION. The lines arrive on the call's own data
 * channel, published by the recorder as each segment comes back from the
 * transcriber — the same connection already carrying the media, already
 * authenticated, already knowing who is in the room. An HTTP endpoint would have
 * meant a second auth path and a second thing to proxy, to deliver text that the
 * SFU was in a position to hand over for free.
 *
 * Which also decides who can see it: whoever is in the call. Nothing here grants
 * access to a meeting somebody is not already in.
 *
 * FOLLOWS THE CONVERSATION UNLESS YOU SCROLL BACK. Jumping someone to the bottom
 * while they are reading what was said a minute ago is the whole reason live
 * panels get closed.
 */
function LiveTranscript() {
  const room = useRoomContext();
  const [lines, setLines] = useState<TranscriptLine[]>([]);
  /** Utterances still being spoken: the newest partial of each, by uttKey.
   * Kept apart from `lines` because they are replaced rather than appended,
   * and because they are not the transcript — the finals are. */
  const [partials, setPartials] = useState<Map<string, TranscriptLine>>(() => new Map());
  const scroller = useRef<HTMLElement | null>(null);
  const following = useRef(true);
  /** Where WE last scrolled the panel to, to tell our scroll from the user's. */
  const autoTop = useRef(0);

  useEffect(() => {
    const onData = (
      payload: Uint8Array,
      _participant?: Participant,
      _kind?: unknown,
      topic?: string,
    ) => {
      if (topic !== TRANSCRIPT_TOPIC) return;
      let line: TranscriptLine;
      try {
        line = JSON.parse(new TextDecoder().decode(payload)) as TranscriptLine;
      } catch {
        return; // not ours, or not JSON; either way not a transcript line
      }
      const keyed = typeof line?.track === 'string' && typeof line?.utt === 'number';
      if (line?.type === 'partial') {
        if (!keyed || !line.text) return;
        // Updated in place: the newest partial of an utterance replaces the
        // last. Partials travel LOSSY, so one can be overtaken, and an older
        // one arriving late is dropped rather than shown going backwards.
        setPartials((prev) => {
          const cur = prev.get(uttKey(line));
          if (cur && !newerPartial(line, cur)) return prev;
          return new Map(prev).set(uttKey(line), line);
        });
        return;
      }
      const isText = line?.type === 'transcript' && !!line.text;
      const isGap = line?.type === 'gap';
      const isNote = line?.type === 'note' && !!line.text;
      if (!isText && !isGap && !isNote) return;
      if (keyed && (isText || isGap)) {
        // The final replaces its partial — and any older partial on the same
        // track whose final never came, since that utterance is over too.
        setPartials((prev) => {
          let next: Map<string, TranscriptLine> | null = null;
          for (const [k, p] of prev) {
            if (p.track === line.track && (p.utt ?? 0) <= (line.utt ?? 0)) {
              next ??= new Map(prev);
              next.delete(k);
            }
          }
          return next ?? prev;
        });
      }
      setLines((prev) =>
        // Sorted on arrival: segments from two people talking over each other
        // are transcribed independently and can land out of order. A live
        // transcript that reads out of sequence is worse than a slow one.
        [...prev, line].sort((a, b) => a.at - b.at).slice(-TRANSCRIPT_WINDOW),
      );
    };
    room.on(RoomEvent.DataReceived, onData);
    return () => {
      room.off(RoomEvent.DataReceived, onData);
    };
  }, [room]);

  useEffect(() => {
    // The aside's OWN scrollTop, not scrollIntoView: that scrolls every
    // scrollable ancestor into line as well, so each new caption dragged the
    // page and the video stage down with it (MN-13).
    const el = scroller.current;
    if (following.current && el) {
      el.scrollTop = el.scrollHeight;
      autoTop.current = el.scrollTop;
    }
  }, [lines, partials]);

  const onScroll = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    // OUR OWN SCROLL IS NOT THE USER LEAVING. The scroll event from the
    // scrollTop set above is delivered after the next burst of lines has
    // already grown the list, so measured then it looks like a gap of many
    // lines and used to switch following off — 20 captions at once and the
    // panel stopped a screen short of the bottom. Only a position ABOVE where
    // we put it is someone scrolling back to read. Only while following:
    // once a reader has scrolled away, autoTop is stale, and reaching it is
    // not reaching the bottom — that is left to the test below.
    if (following.current && el.scrollTop >= autoTop.current - 1) return;
    // A small slack, because a browser's scroll maths does not always land
    // exactly on the bottom and being one pixel short must not read as "the
    // user has scrolled away".
    following.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
  }, []);

  // Partials sit among the finals at their place in the meeting; mergeFragments
  // joins only finals, so a partial is always its own row and keeps its key.
  const rows = useMemo(
    () => mergeFragments([...lines, ...partials.values()].sort((a, b) => a.at - b.at)),
    [lines, partials],
  );

  return (
    // BOTH CLASSES ARE LOAD-BEARING, and not only for styling. `.mx-rail` is
    // what gives this panel the rail's padding, border and type; theme.css then
    // hides the recording rail on a narrow screen with
    // `.mx-rail:not(.mx-rail-transcript)` specifically so this one is NOT caught
    // as collateral — it moves under the video instead, because subtitles are
    // the panel a phone needs most. Drop `.mx-rail` here and this silently
    // loses its styling; drop `.mx-rail-transcript` and it silently vanishes on
    // every phone. Neither shows up on a desktop, which is where it would be
    // changed.
    <aside
      className="mx-rail mx-rail-transcript"
      aria-label="Live transcript"
      ref={scroller}
      onScroll={onScroll}
    >
      <div className="mx-rail-head">
        <span>Saying</span>
        <span>{lines.length === TRANSCRIPT_WINDOW ? `last ${TRANSCRIPT_WINDOW}` : lines.length}</span>
      </div>

      {lines.length === 0 && partials.size === 0 ? (
        <p className="mx-rail-empty">
          Transcribing as you speak. The first lines appear a few seconds behind the
          conversation.
        </p>
      ) : (
        <ol className="mx-tx">
          {rows.map((l, i) =>
            l.type === 'note' ? (
              // A running note, filed at the moment it covers up to — so it
              // sits among the lines it was written from rather than at the
              // bottom, which is where somebody reading along is looking.
              <li className="mx-tx-note" key={`note-${l.at}-${i}`}>
                <span className="mx-tx-note-who">Notes so far</span>
                <span className="mx-tx-note-text">{l.text}</span>
              </li>
            ) : l.type === 'gap' ? (
              // Transcription fell behind far enough that this stretch would
              // have arrived as a caption about the wrong moment. Showing the
              // break is honest; showing the words late is not.
              <li className="mx-tx-gap" key={`gap-${l.at}-${i}`}>
                {l.speaker} spoke here — still transcribing; it will be in the full
                transcript.
              </li>
            ) : l.type === 'partial' ? (
              // Still being said: greyed, and rewritten in place under a key
              // that stays the same until the final takes its place. The part
              // the transcriber has committed to is a shade firmer than the
              // tail that may still change.
              <li className="mx-tx-line mx-tx-partial" key={`p-${uttKey(l)}`} aria-live="off">
                <span className="mx-tx-who">{l.speaker}</span>
                <PartialText text={l.text ?? ''} upto={l.stable_upto ?? 0} />
              </li>
            ) : (
              <li className="mx-tx-line" key={`${l.at}-${l.speaker}-${i}`}>
                <span className="mx-tx-who">{l.speaker}</span>
                <span className="mx-tx-text">{l.text}</span>
              </li>
            ),
          )}
        </ol>
      )}
    </aside>
  );
}

/**
 * Is this error about a camera/microphone rather than about the connection?
 *
 * getUserMedia rejects with a DOMException whose `name` says which way it went:
 * NotFoundError (no such device — a desktop with no webcam), NotAllowedError
 * (permission refused), NotReadableError (another app holds it),
 * OverconstrainedError, AbortError, SecurityError. None of them is a reason to
 * leave a meeting that is already connected.
 */
const MEDIA_ERROR_NAMES = new Set([
  'NotFoundError',
  'NotAllowedError',
  'NotReadableError',
  'OverconstrainedError',
  'AbortError',
  'SecurityError',
  'PermissionDeniedError', // legacy alias still seen in the wild
]);

function isMediaDeviceError(e: unknown): boolean {
  if (typeof DOMException !== 'undefined' && e instanceof DOMException) return true;
  const name = (e as { name?: string } | null)?.name;
  return typeof name === 'string' && MEDIA_ERROR_NAMES.has(name);
}

/**
 * Turns the camera and microphone on AFTER the room connection is established,
 * and treats failure as a warning rather than an exit.
 *
 * This is the half that keeps a denied permission prompt from locking someone
 * out of a meeting. By the time this runs the user is already a participant, so
 * the worst case is that they are in the call and can hear everyone — which is
 * a normal way to attend one.
 *
 * The message deliberately names the likely fix. "Client initiated disconnect"
 * (what the old fatal path produced) told the user nothing at all.
 */
function EnableDevices() {
  const room = useRoomContext();
  const [failed, setFailed] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // NOT BEFORE THE ROOM IS CONNECTED. LiveKitRoom applies its own
      // `audio={false} video={false}` on SignalConnected by calling
      // setCameraEnabled(false) and setMicrophoneEnabled(false), so a camera
      // that came up before that — a returning user whose permission is
      // already granted, on a fast device — was published and then muted
      // again a moment later, and they joined with everything off.
      // `Connected` fires after SignalConnected, so waiting for it means
      // that sync has already run.
      if (room.state !== ConnectionState.Connected) {
        await new Promise<void>((resolve) => room.once(RoomEvent.Connected, () => resolve()));
      }
      if (cancelled) return;
      // ONCE PER ROOM, NOT ONCE PER MOUNT. enableCameraAndMicrophone creates
      // and publishes NEW tracks every time it is called — it does not look
      // for the ones already published. So anything that remounted this
      // component inside a live room published a second camera and a second
      // microphone, and everyone else saw this person twice. If either device
      // is already published, it has been done; the toggles own it from here.
      const lp = room.localParticipant;
      if (lp.getTrackPublication(Track.Source.Camera) || lp.getTrackPublication(Track.Source.Microphone)) {
        return;
      }
      try {
        await room.localParticipant.enableCameraAndMicrophone();
      } catch (e) {
        if (cancelled) return;
        // Fall back to audio only: a missing or refused CAMERA should not also
        // cost someone their microphone.
        try {
          await room.localParticipant.setMicrophoneEnabled(true);
          setFailed('camera');
        } catch {
          setFailed('both');
        }
        console.warn('media devices:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [room]);

  if (!failed || dismissed) return null;

  // The operator's help page, if they named one. Omitted entirely when they did
  // not: sending someone whose camera is already broken to a page that does not
  // exist is worse than offering nothing, which is the contract
  // `branding.supportURL` is documented under and the reason it is nullable.
  const support = brand().supportURL;

  return (
    <div className="mx-devices" role="status">
      <span>
        {failed === 'camera'
          ? "We couldn't start your camera, so you've joined with microphone only."
          : "We couldn't start your camera or microphone, so you've joined as a listener."}{' '}
        Check the camera icon in your browser's address bar, close any other app using the
        device, then use the controls below to turn it on.
        {support && (
          <>
            {' '}
            <a className="mx-support-link" href={support} target="_blank" rel="noreferrer">
              More help
            </a>
          </>
        )}
      </span>
      <button className="mx-devices-close" onClick={() => setDismissed(true)} aria-label="Dismiss">
        ×
      </button>
    </div>
  );
}

/** How long the notice stays up. A live region has already announced it. */
const REMOTE_MUTE_NOTICE_MS = 12000;

/** The RPC a granted comrade's control arrives on (cmd/portal/grant.go). */
const CONTROL_METHOD = 'meetnix.control';

interface ControlRequest {
  kind?: string;
  source?: string;
  muted?: boolean;
  presence?: string;
  name?: string;
  at?: number;
}

/** The SFU's own channel for an operator's mute or unmute (MWE-22). */
interface RemoteMuteSource {
  on(event: 'remoteMute', fn: (trackSid: string, muted: boolean) => void): unknown;
  off(event: 'remoteMute', fn: (trackSid: string, muted: boolean) => void): unknown;
}

/**
 * Says so when an operator turns this person's microphone or camera on or off
 * from the host (meetnixctl call ROOM mute/unmute, MWE-9/MWE-17).
 *
 * WITHOUT THIS THE MIC JUST COMES BACK ON. LiveKit applies a server mute or
 * unmute to the local track itself, and the dock button follows, but nothing
 * tells the person why: measured on telegonus (Q-2069), a server unmute left
 * the page exactly as it was. Being made audible without a word is the one
 * thing a person must hear about.
 *
 * The SDK raises no public event for it: a self-mute and a server one both end
 * in TrackMuted/TrackUnmuted. Only the engine's `remoteMute` (the signal's
 * MuteTrackRequest) tells them apart, so we listen there. The engine is the
 * room's and is replaced when a connection is retried, hence the re-attach on
 * every (re)connect. web/e2e's "remote mute" checks fail if an SDK upgrade
 * moves it.
 *
 * And it says so when a comrade the person let control their client turns a
 * track on or off for them (MWE-27): "NAME turned your microphone on."
 */
function RemoteMuteNotice({ tuneKey }: { tuneKey?: string }) {
  const room = useRoomContext();
  const [notice, setNotice] = useState<{ text: string; n: number } | null>(null);

  useEffect(() => {
    let n = 0;
    const onRemoteMute = (trackSid: string, muted: boolean) => {
      const pub = room.localParticipant.trackPublications.get(trackSid);
      const what =
        pub?.source === Track.Source.Camera
          ? 'camera'
          : pub?.source === Track.Source.Microphone
            ? 'microphone'
            : pub?.source === Track.Source.ScreenShare || pub?.source === Track.Source.ScreenShareAudio
              ? 'screen share'
              : 'a track you send';
      const yours = what === 'a track you send' ? what : `your ${what}`;
      setNotice({ text: `The host turned ${yours} ${muted ? 'off' : 'on'}.`, n: ++n });
    };
    let attached: RemoteMuteSource | null = null;
    const attach = () => {
      const engine = room.engine as unknown as RemoteMuteSource | undefined;
      if (engine === attached) return;
      attached?.off('remoteMute', onRemoteMute);
      engine?.on('remoteMute', onRemoteMute);
      attached = engine ?? null;
    };
    attach();
    room.on(RoomEvent.Connected, attach).on(RoomEvent.Reconnected, attach).on(RoomEvent.SignalReconnecting, attach);
    return () => {
      room.off(RoomEvent.Connected, attach).off(RoomEvent.Reconnected, attach).off(RoomEvent.SignalReconnecting, attach);
      attached?.off('remoteMute', onRemoteMute);
    };
  }, [room]);

  // A comrade the person let control their own client (MWE-27): the portal
  // relays its request, signed for this browser alone, and the browser turns
  // its own track on or off. Only what this browser granted (grants.ts):
  // the portal's say-so is not enough.
  useEffect(() => {
    if (!tuneKey) return; // a portal that predates it: nothing to answer
    let n = 1_000_000; // apart from the host's notices' keys
    room.registerRpcMethod(CONTROL_METHOD, async (data) => {
      const req = await verifiedSigned<ControlRequest>(tuneKey, data.payload);
      if (req?.kind !== 'control') throw new RpcError(1403, 'not a control request from the portal');
      const scope = req.source === 'microphone' ? 'mic' : req.source === 'camera' ? 'camera' : null;
      if (!scope || typeof req.muted !== 'boolean' || !req.presence) throw new RpcError(1400, 'not a control this client knows');
      if (!granted(req.presence, scope))
        throw new RpcError(1403, `this person has not let ${req.name ?? req.presence} control their ${req.source}`);
      const lp = room.localParticipant;
      if (scope === 'mic') await lp.setMicrophoneEnabled(!req.muted);
      else await lp.setCameraEnabled(!req.muted);
      const pub = lp.getTrackPublication(scope === 'mic' ? Track.Source.Microphone : Track.Source.Camera);
      setNotice({ text: `${req.name ?? 'An assistant'} turned your ${req.source} ${req.muted ? 'off' : 'on'}.`, n: ++n });
      return JSON.stringify({ muted: pub ? pub.isMuted : true });
    });
    return () => room.unregisterRpcMethod(CONTROL_METHOD);
  }, [room, tuneKey]);
  // The grants were for this join; a new one starts with none.
  useEffect(() => () => forgetGrants(), []);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), REMOTE_MUTE_NOTICE_MS);
    return () => clearTimeout(t);
  }, [notice]);

  // The live region is always mounted: one that appears with its text in it is
  // not reliably announced.
  return (
    <div className="mx-remote-mute" role="status" aria-live="polite" hidden={!notice}>
      {notice && (
        <>
          <span key={notice.n}>{notice.text}</span>
          <button type="button" className="mx-devices-close" onClick={() => setNotice(null)} aria-label="Dismiss">
            ×
          </button>
        </>
      )}
    </div>
  );
}

interface CallProps {
  grant: JoinGrant;
  me: string;
  onLeave: () => void;
  navigate: (r: Route) => void;
}

/** The meeting itself. */
export function Call({ grant, me, onLeave, navigate }: CallProps) {
  const [error, setError] = useState<Error | null>(null);
  // The side panels (what is being written, what is being said) are open by
  // default — the disclosure is the point of them — but they are a person's to
  // close when the video is what they want the width for.
  const [railsOpen, setRailsOpen] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  // Did we ever actually get in? A ref, not state, because the disconnect
  // handler has to read the CURRENT value rather than the one captured when it
  // was created.
  const connected = useRef(false);

  // LEAVING AND NEVER ARRIVING ARE DIFFERENT THINGS.
  //
  // LiveKit reports both as onDisconnected. Treating them the same sent someone
  // whose connection failed straight back to the dashboard with no explanation —
  // the same class of dead end as the old "client initiated disconnect", which
  // told the user nothing and offered nothing. So: a disconnect AFTER connecting
  // is a departure; a disconnect BEFORE connecting is a failure, and gets said
  // out loud.
  const handleDisconnected = useCallback(() => {
    if (connected.current) {
      onLeave();
      return;
    }
    setError(
      (prev) =>
        prev ??
        new Error(
          'The meeting server did not answer. It may be restarting — try again in a moment.',
        ),
    );
  }, [onLeave]);

  const handleError = useCallback((e: Error) => {
    // A CAMERA PROBLEM IS NOT A CONNECTION PROBLEM.
    //
    // LiveKit routes media-device failures through onError as well as through
    // onMediaDeviceFailure. Treating everything here as fatal meant that a
    // machine with no webcam — which reports DOMException NotFoundError, "The
    // object can not be found here" in Firefox — had its perfectly healthy room
    // connection torn down and replaced with "Could not join the call". The
    // audio-only fallback in EnableDevices was already working; this handler was
    // unmounting the room out from under it.
    //
    // Device failures are handled where the devices are handled. Everything else
    // — a dead WebSocket, a rejected token, an ICE failure — is genuinely fatal
    // and still lands on the error card.
    if (isMediaDeviceError(e)) {
      console.warn('livekit: media device unavailable, staying in the call:', e);
      return;
    }
    console.error('livekit:', e);
    setError(e);
  }, []);

  if (error) {
    return (
      <div className="mx-centre">
        <div className="mx-card">
          <Mark />
          <h1 className="mx-error">Could not join the call</h1>
          <p>{error.message}</p>
          <button className="mx-button" onClick={() => window.location.reload()}>
            Try again
          </button>
          {/* A way out that is not the back button. Whatever is wrong with the
              SFU, the summaries and the status page still work. */}
          <button className="mx-invite-btn" onClick={() => navigate({ name: 'dashboard' })}>
            Back to {brand().shortName}
          </button>
        </div>
      </div>
    );
  }

  return (
    <LiveKitRoom
      serverUrl={grant.url}
      token={grant.token}
      connect
      // NOT `audio video`. Those make LiveKit acquire the camera and microphone
      // as part of CONNECTING, so a denied permission prompt, a webcam held by
      // another app, or a machine with no camera aborts the whole connection —
      // the user lands on "Could not join the call: Client initiated disconnect",
      // which names neither the cause nor a way forward. That is a dead end for
      // something as ordinary as clicking "Block" once.
      //
      // Instead: join first, then try to turn the devices on (see EnableDevices).
      // Failing to get a camera is not a reason to be kept out of a meeting —
      // people join to listen.
      audio={false}
      video={false}
      onConnected={() => {
        connected.current = true;
      }}
      onDisconnected={handleDisconnected}
      onError={handleError}
      // The dedicated channel for device trouble. Handled (and surfaced to the
      // user) by EnableDevices; logged here so it is visible in the console
      // without also being routed to the fatal path.
      onMediaDeviceFailure={(failure, kind) =>
        console.warn('livekit: media device failure', { failure, kind })
      }
      // Fill the flex parent rather than the viewport; the top bar and the
      // recording banner are siblings, and 100vh here would push them off.
      style={{ height: '100%' }}
      data-lk-theme="default"
    >
      <LayoutContextProvider onWidgetChange={(w) => setChatOpen(w.showChat)}>
        <div className="mx-shell">
          <header className="mx-topbar">
            <Mark />
            <span className="mx-room">{grant.title || grant.room}</span>
            <CallMeta />
            <span className="mx-spacer" />
            {/* Guests get neither the archive nor the status page — the portal
                refuses them, so the buttons are not rendered rather than rendered
                and failing. Leaving the call this way disconnects cleanly: the
                room unmounts, which is a normal departure, not a dropped call. */}
            {grant.role !== 'guest' && (
              <>
                <button className="mx-invite-btn" onClick={() => navigate({ name: 'dashboard' })}>
                  Home
                </button>
                <button className="mx-invite-btn" onClick={() => navigate({ name: 'meetings' })}>
                  Meetings
                </button>
                <button
                  className="mx-invite-btn"
                  onClick={() => navigate({ name: 'diagnostics' })}
                  title="Is the pipeline healthy?"
                >
                  Status
                </button>
              </>
            )}
            <InviteButton room={grant.room} canInvite={grant.role !== 'guest'} />
            <span className="mx-who">{me}</span>
            {grant.role === 'op' && <span className="mx-badge mx-badge-op">op</span>}
            {grant.role === 'guest' && <span className="mx-badge">guest</span>}
          </header>

          <RecordingBanner />
          <EnableDevices />
          <RemoteMuteNotice tuneKey={grant.tune_key} />

          <div className="mx-call">
            <div className="mx-stage">
              {/* Our own stage rather than LiveKit's prebuilt <VideoConference/>:
                  the other person fills the screen, you float in a corner, and
                  the controls sit on the video. See Stage.tsx. */}
              <CallStage
                dockExtra={<QualityMenu tuneKey={grant.tune_key} />}
                tileOverlay={statsOverlay}
                dockPanels={
                  <>
                    <button
                      type="button"
                      className="mx-dock-btn"
                      aria-pressed={railsOpen}
                      aria-label={railsOpen ? 'Hide transcript' : 'Show transcript'}
                      title={railsOpen ? 'Hide transcript' : 'Show transcript'}
                      onClick={() => setRailsOpen((o) => !o)}
                    >
                      <span className="mx-dock-cc" aria-hidden="true">
                        CC
                      </span>
                    </button>
                    <ChatToggle className="mx-dock-btn" aria-label="Chat" title="Chat">
                      <ChatIcon />
                    </ChatToggle>
                  </>
                }
              />
            </div>
            {/* Beside the call rather than under it: the point of the panels is
                that the ordinary thing and the unusual thing are visible at the
                same time. They hide themselves on a narrow screen — see
                theme.css. What is being written, above what is being said.
                Hidden rather than unmounted when closed, so the transcript keeps
                collecting lines and is all there when it is opened again. */}
            <div className="mx-rails" hidden={!railsOpen && !chatOpen}>
              <div className="mx-rails-inner" hidden={!railsOpen}>
                <RecordingRail room={grant.room} />
                <LiveTranscript />
              </div>
              {/* Always mounted, hidden when closed: LiveKit keeps the chat
                  history inside the component, so unmounting it threw away
                  what had been said and dropped anything sent while closed.
                  Wrapped, because LiveKit's Chat sets its own className and
                  does not pass `hidden` through. */}
              <div className="mx-rail mx-chat" hidden={!chatOpen}>
                <Chat />
              </div>
            </div>
          </div>
        </div>
      </LayoutContextProvider>

      {/* Plays every remote audio track. Without it the call is silent and the
          cause is entirely non-obvious — video works fine. */}
      <RoomAudioRenderer />
    </LiveKitRoom>
  );
}
